import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, TableCell, TableRow, Typography } from '@mui/material';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { API } from '../../../../../../api';
import { useParams } from 'react-router-dom';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import InviteUserDialog from '../InviteUserDialog';
import ProgramDeletDialog from '../ProgramDeletDialog';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DeleteIcon } from '../../../../../../utils/Icons';
import { useAppDispatch } from '../../../../../../redux/hooks';
import ChatAvatarComponent from '../../../chat/ChatAvatarComponent';
import { IGroupInvitedUser, groupUsersActions } from '../../../../../../redux/slices/group-users/groupUsersSlice';
import { MemberRole, isMemberRole, useMemberWord } from '../../../../../../utils/memberTypeLabels';

interface Props {
  data: IGroupInvitedUser;
  showGoalProgress?: boolean;
  showCompany?: boolean;
}

const ProgramInvitedUsers = ({ data, showGoalProgress, showCompany }: Props) => {
  const dispatch = useAppDispatch();
  const { orgId } = useProgramContext();
  const w = useMemberWord();
  const firstRole = data.role?.[0];

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openDelete, setDelete] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleResendInvitation = () => {
    handleClose();
    setOpenDialog(true);
  };

  const handleRevokeClick = () => {
    handleClose();
    setDelete(true);
  };

  const enrolledCount = data.groups?.[0]?.enrolledCount ?? 'None';
  const primaryRole = data.role?.[0] ?? 'Not set';

  const COLS = {
    checkbox: 22,
    name: 200,
    role: 150,
    lastLogin: 150,
    programs: 80,
    onboarding: 150,
    onboardedDate: 150,
    goalProgress: 140,
    experience: 150,
    meetings: 100,
    actions: 220
  };

  const handleRevokeInvitation = async () => {
    setDeleteLoader(true);
    try {
      const response = await API.revokeGroupInvitation({
        orgId,
        invitedId: data.invitationId
      });

      if (response.status === 200) {
        dispatch(
          groupUsersActions.removeInvitedUser({
            invitationId: data.invitationId
          })
        );
        toast.success('Invitation revoked.');
      }
    } catch {
      toast.error('The invitation could not be revoked. Try again.');
    }
    setDeleteLoader(false);
  };

  return (
    <>
      <TableRow
        sx={{
          height: 65,
          borderBottom: '1px solid #2A2A2A',
          bgcolor: '#151515',
          color: '#FDFDFD',
          '&:hover': { bgcolor: '#151515' }
        }}
      >
        {/* 1 — checkbox */}
        <TableCell sx={{ width: COLS.checkbox }} />

        {/* 2 — NAME */}
        <TableCell sx={{ width: COLS.name }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
            {data.firstName} {data.lastName}
          </Typography>
        </TableCell>

        {/* 3 — ROLE */}
        <TableCell sx={{ width: COLS.role }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
            {isMemberRole(firstRole)
              ? w(firstRole.trim().toLowerCase() as MemberRole)
              : _.startCase(firstRole ?? 'N/A')}
          </Typography>
        </TableCell>

        {/* 4 — LAST LOGIN */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.lastLogin }}>N/A</TableCell>

        {/* 5 — PROGRAMS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.programs, textAlign: 'center' }}>
          {data.groups?.[0]?.enrolledCount ?? 'None'}
        </TableCell>

        {/* 6 — ONBOARDING STATUS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.onboarding }}>Invited</TableCell>

        {/* 7 — ONBOARDED DATE */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.onboardedDate, textAlign: 'center' }}>N/A</TableCell>

        {/* 8 — GOAL PROGRESS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.goalProgress, textAlign: 'center' }}>N/A</TableCell>

        {/* 9 — XP */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.experience, textAlign: 'center' }}>N/A</TableCell>

        {/* 10 — MEETINGS */}
        <TableCell sx={{ color: '#FDFDFD', width: COLS.meetings, textAlign: 'center' }}>N/A</TableCell>

        {/* 11 — ACTIONS */}
        <TableCell
          sx={{
            color: '#FDFDFD',
            width: COLS.actions,
            minWidth: COLS.actions,
            position: 'sticky',
            right: 0,
            zIndex: 1,
            bgcolor: '#151515',
            boxShadow: '-4px 0 8px rgba(0,0,0,0.3)'
          }}
        >
          <IconButton
            size="small"
            onClick={handleOpen}
            aria-describedby={id}
            aria-haspopup="true"
            aria-label="Open menu"
            sx={{ cursor: 'pointer' }}
          >
            <MoreVertIcon sx={{ color: '#FDFDFD', fontSize: 20 }} />
          </IconButton>
        </TableCell>
      </TableRow>

      <Menu
        id={id}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { bgcolor: '#1C1C1C', color: '#FDFDFD' } } }}
      >
        <MenuItem onClick={handleResendInvitation}>Resend invitation</MenuItem>
        <MenuItem onClick={handleRevokeClick} sx={{ color: '#f44336' }}>
          Revoke invitation
        </MenuItem>
      </Menu>

      <ProgramDeletDialog
        openDelete={openDelete}
        setDelete={setDelete}
        deleteLoader={deleteLoader}
        text="Are you sure you want to revoke the invitation?"
        handleDelete={handleRevokeInvitation}
        btnText="Yes, Revoke"
        loadingText="Revoking..."
      />

      <InviteUserDialog
        handleClose={handleClose}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        data={data}
        type="invite"
      />
    </>
  );
};

export default ProgramInvitedUsers;
